<template>
  <div
    :class="`edit-link-popup ${editorId}`"
    @mousedown="hidePopup"
    @blur.stop="hidePopup"
    @keydown.esc.stop="hidePopup"
  >
    <div
      class="popup-window add-link-popup"
      @mousedown.stop
    >
      <PopupHeader
        :title="youtubeVideoPopup ? translate('embedVideo') : translate('addLink')"
        @close="hidePopup"
      />
      <div
        ref="deleteCommentBody"
        class="add-link-popup-body"
      >
        <div class="edit-link-input">
          <StInput
            v-if="!youtubeVideoPopup"
            ref="linkText"
            :key="'link-text-field'"
            :value="currentText"
            :text-value="currentText"
            :label-text="'linkTextLabel'"
            :placeholder-text="'linkTextPlaceholder'"
            :input-type="'text'"
            :error-message="'Please enter valid email address.'"
            :input-width="468"
            @input="currentText = $event"
            @enter-key="enterHandlerFocusNext"
          />
          <StInput
            ref="linkUrl"
            :key="'link-url-field'"
            :value="linkUrl"
            :text-value="linkUrl"
            :label-text="'linkUrlLabel'"
            :placeholder-text="'linkUrlPlaceholder'"
            :input-type="'text'"
            :error-message="'Please enter valid email address.'"
            :input-width="468"
            @input="linkUrl = $event"
            @enter-key="saveEditedLink"
          />
        </div>
      </div>
      <div class="buttons-wrap">
        <ButtonCustom
          class="text-sm font-normal rounded border-important-border"
          :text="translate('cancel')"
          :type="'popup-button-wrap important'"
          @click="hidePopup"
        />
        <div class="empty-separator" />
        <ButtonCustom
          class="text-sm font-normal rounded"
          :text="translate(buttonLabel)"
          :type="'popup-button-wrap save'"
          @click="saveEditedLink"
        />
      </div>
    </div>
  </div>
</template>

<script>
import ButtonCustom from '@/components/elements/ButtonCustom';
import StInput from '@/components/elements/StInput';
import linkRegex from '@/mixins/linkRegex';
import PopupHeader from '@/components/elements/PopupHeader';

export default {
  name: 'PopUpEditLink',

  components: {
    ButtonCustom,
    StInput,
    PopupHeader,
  },

  mixins: [linkRegex],

  props: {
    insertLink: {
      type: Boolean,
      required: false,
      default: false,
    },

    youtubeVideoPopup: {
      type: Boolean,
      required: false,
      default: false,
    },

    editorId: {
      type: String,
      required: true,
    },

    selectedText: {
      type: String,
      required: false,
      default: '',
    },

    from: {
      type: Number,
      required: true,
    },

    to: {
      type: Number,
      required: true,
    },

    popup: {
      type: Object,
      required: true,
    },

    url: {
      type: String,
      requried: false,
      default: '',
    },
  },

  data() {
    return {
      editLinkInput: false,
      insertLinkText: '',
      insertLinkUrl: '',
      linkUrl: '',
      currentText: '',
    };
  },
  computed: {
    buttonLabel() {
      return this.insertLink && this.isEmptyInput ? 'insert' : 'save';
    },
    isEmptyInput() {
      return this.currentText === '' && this.linkUrl === '';
    },
    isSelectedText() {
      return this.currentText && !this.linkUrl;
    },
    focusLinkText() {
      return this.currentText === '' && this.linkUrl === '';
    },
    focusLinkUrl() {
      return ((this.linkUrl || !this.linkUrl) && this.currentText) || this.youtubeVideoPopup;
    },

    editor() {
      return this.$store.getters.editor(this.editorId) || {};
    },
  },

  created() {
    if (this.isSelectedText || this.isEmptyInput || this.youtubeVideoPopup) {
      this.editLinkInput = true;
    }
  },

  beforeUnmount() {
    window.removeEventListener('popstate', this.hidePopup);
  },

  mounted() {
    window.addEventListener('popstate', this.hidePopup);
    this.currentText = this.selectedText;
    if (this.url !== '') {
      this.linkUrl = this.url;
    }
    if (this.focusLinkText && !this.youtubeVideoPopup) {
      this.$refs.linkText.focusInput();
    } else if (this.focusLinkUrl) {
      this.$refs.linkUrl.focusInput();
    }
  },

  unmounted() {
    if (this.youtubeVideoPopup) {
      this.$store.dispatch('setCommentLinkUrl', '');
    }
  },
  methods: {
    hidePopup() {
      this.popup[0].unmount();
      this.editor.commands?.focus();
    },

    editLink() {
      this.editLinkInput = true;
    },
    saveEditedLink() {
      if (this.youtubeVideoPopup) {
        this.addYoutubeEmbed(this.linkUrl);
        this.hidePopup();
        return;
      }
      // if text has changed update content and set selection to new text.
      if (this.currentText !== this.selectedText && this.currentText !== '') {
        this.editor.commands.insertContentAt({ from: this.from, to: this.to }, this.currentText);
        this.editor.commands.setTextSelection({
          from: this.from,
          to: this.from + this.currentText.length,
        });
      }

      if (this.linkUrl === '') {
        this.editor.commands.setTextSelection({
          from: this.from,
          to: this.from + this.currentText.length,
        });

        this.editor.commands.unsetMark('link');
        this.hidePopup();
        return;
      }

      if (this.currentText === '') {
        this.editor.commands.deleteRange({ from: this.from, to: this.to });
        this.editor.commands.insertContentAt({ from: this.from }, this.linkUrl);
        this.editor.commands.setTextSelection({
          from: this.from,
          to: this.from + this.linkUrl.length,
        });
      }

      if (this.currentText === this.selectedText) {
        this.editor.commands.setTextSelection({
          from: this.from,
          to: this.to,
        });
      }

      const modified = this.checkUrl(this.linkUrl);

      this.editor.chain().focus().extendMarkRange('link').setLink({ href: modified }).run();

      this.hidePopup();
    },

    enterHandlerFocusNext() {
      if (this.linkUrl === '') {
        this.$nextTick(() => {
          this.$refs.linkUrl.focusInput();
        });
      }
    },

    checkUrl(text) {
      const linkMatch = text.match(this.urlRegularExpression);
      const mailMatch = text.match(this.mailRegularExpression);
      const phoneMatch = text.match(this.phoneRegularExpresion);
      const anchorMatch = text.match(this.anchorRegularExpression);
      if (anchorMatch) {
        const [host, hash] = text.split('#');
        const [locationHost] = window.location.href.split('#');
        if (host && host === locationHost) {
          return `#${hash}`;
        }
      }
      if (mailMatch) {
        return this.modifyMail(text);
      }
      if (linkMatch) {
        return this.modifyLink(text);
      }
      if (phoneMatch) {
        return this.modifyPhone(text);
      }
      return text;
    },
    modifyLink(url) {
      const linkRule = /^(http(s?)):\/\//i;
      if (!linkRule.test(url)) {
        return this.addHttpsPrefix(url);
      }
      return url;
    },
    modifyMail(url) {
      const mailRule = /^(mailto:)/i;
      if (!mailRule.test(url)) {
        return this.addMailPrefix(url);
      }
      return url;
    },
    modifyPhone(url) {
      const phoneRule = /^(tel:)/i;
      if (!phoneRule.test(url)) {
        return this.addTelPrefix(url);
      }
      return url;
    },
    addHttpsPrefix(url) {
      return `https://${url}`;
    },
    addMailPrefix(url) {
      return `mailto:${url}`;
    },
    addTelPrefix(url) {
      return `tel:${url}`;
    },

    addYoutubeEmbed(src) {
      this.editor.commands.setYoutubeVideo({
        src,
        width: 640,
        height: 480,
      });
    },
  },
};
</script>

<style lang="scss">
.edit-link-popup {
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(3, 7, 11, 0.65); /* Black w/ opacity */

  .popup-window {
    &.add-link-popup {
      display: flex;
      flex-direction: column;
      width: 35%;
      .add-link-popup-header {
        display: flex;
        flex-direction: row;
        align-items: center;
        border-bottom: 1px solid #ededed;
        padding: 19px 21px 19px 16px;
        .close-popup-button {
          display: flex;
          margin-left: auto;
        }
        .popup-header-message {
          font-size: 16px;
          font-weight: 500;
          line-height: 20px;
          letter-spacing: 0.3px;
        }
      }
      .add-link-popup-body {
        max-height: 465px;
        overflow-y: auto;
        @apply px-6 pt-6 pb-2;
        .popup-body-message {
          font-size: 14px;
          line-height: 19px;
          font-weight: 500;
          letter-spacing: 0.2px;
          padding: 0px 10px 16px 10px;
        }
        .comment-to-delete-preview {
          display: flex;
          flex-direction: row;
          border: 1px solid #dadcdf;
          border-radius: 8px;
          padding: 16px;
          margin: 0px 18px 24px 18px;
          .posted-user-comment {
            margin-top: 6px;
            margin-left: 7px;
          }
        }
        .input-wrap {
          .input-field-label {
            @apply text-text-default;
          }
          .input-field {
            @apply bg-standard-bg border border-solid border-standard-border text-text-default;
            &:focus {
              @apply border-standard-borderFocused;
            }
            &::placeholder {
              @apply text-text-placeholder;
            }
          }
        }
      }
      .buttons-wrap {
        display: flex;
        justify-content: flex-end;
        padding: 16px;
        @apply pr-6;
        &.popup-body-border {
          border-top: 1px solid #ededed;
        }
      }
    }
  }
}

.add-link-popup-body::-webkit-scrollbar {
  width: 6px !important;
}

.mobile {
  .add-link-popup {
    width: 90%;
  }
}
</style>
